import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import staffPicksService, { StaffPick } from '../services/staffPicks';
import dressLikesService from '../services/dressLikes';
import DressCard from '../components/DressCard';

const StaffPicks: React.FC = () => {
  const [staffPicks, setStaffPicks] = useState<StaffPick[]>([]);
  const [likedDressIds, setLikedDressIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadStaffPicks = useCallback(async (pageNum: number, reset: boolean = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const response = await staffPicksService.getStaffPicks(pageNum, 20);

      if (reset) {
        setStaffPicks(response.data);
      } else {
        setStaffPicks(prev => [...prev, ...response.data]);
      }

      setHasMore(response.pagination.page < response.pagination.totalPages);

      // Check which dresses are liked
      const dressIds = response.data.map(pick => pick.dress.id);
      if (dressIds.length > 0) {
        const liked = await dressLikesService.checkLikes(dressIds);
        setLikedDressIds(prev => {
          const newSet = new Set(prev);
          liked.forEach(id => newSet.add(id));
          return newSet;
        });
      }
    } catch (err) {
      console.error('Failed to load staff picks:', err);
      setError('Failed to load staff picks. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadStaffPicks(1, true);
  }, [loadStaffPicks]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setPage(prev => prev + 1);
      loadStaffPicks(page + 1);
    }
  };

  const handleLikeChange = (dressId: string, isLiked: boolean) => {
    setLikedDressIds(prev => {
      const newSet = new Set(prev);
      if (isLiked) {
        newSet.add(dressId);
      } else {
        newSet.delete(dressId);
      }
      return newSet;
    });
  };

  if (loading && page === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => loadStaffPicks(1, true)} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <Link to="/home" className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              <span>Back</span>
            </Link>
          </div>

          <h1 className="text-2xl font-serif text-gray-900">Staff Picks</h1>
          {staffPicks.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Dresses picked just for you
            </p>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="px-4 py-4">
        {staffPicks.length === 0 ? (
          <div className="text-center py-12">
            <div className="mb-4">
              <svg
                className="mx-auto h-16 w-16 text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </div>
            <p className="text-gray-500 mb-2">No staff picks yet</p>
            <p className="text-sm text-gray-400 mb-4">
              Our team will recommend dresses tailored just for you
            </p>
            <Link to="/home/catalog" className="btn-primary inline-block">
              Browse Catalog
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {staffPicks.map((pick) => (
                <div key={pick.id} className="relative">
                  <DressCard
                    dress={pick.dress}
                    isLiked={likedDressIds.has(pick.dress.id)}
                    onLikeChange={handleLikeChange}
                  />
                  {/* Staff Note */}
                  {pick.note && (
                    <div className="mt-2 px-3 py-2 bg-primary-50 rounded-lg border border-primary-100">
                      <p className="text-sm text-primary-700 italic">"{pick.note}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="btn-secondary"
                >
                  {loadingMore ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mx-auto"></div>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StaffPicks;
